import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getHierarchyNodes,
  getEntities,
  updateEntity,
  createEntityAsync,
  getOperationStatus,
  deleteEntity,
  invalidateHierarchyNodesCache,
} from "../services/api";
import HierarchyExcelImport from "./HierarchyExcelImport";
import { clearHierarchyTreeSessionCache } from "./HierarchyMultiSelectTree";
import { createDevConsole } from "../utils/devLogger";
import { CAPABILITY_HIERARCHY_CONFIG } from "../utils/taxonomyHierarchyConfigs";

const console = createDevConsole(process.env.NODE_ENV === "development");
const debugLog = (...args) => console.log(...args);
const debugWarn = (...args) => console.warn(...args);
const debugError = (...args) => console.error(...args);

const DEFAULT_CONFIG = CAPABILITY_HIERARCHY_CONFIG;

/** IDs to remove from flat hierarchy state: deleted node and any loaded descendants. */
const collectDescendantIds = (rootId, nodes) => {
  const remove = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const n of nodes) {
      if (n.parent && remove.has(n.parent) && !remove.has(n.id)) {
        remove.add(n.id);
        grew = true;
      }
    }
  }
  return remove;
};

const CapabilityHierarchyBuilder = ({
  config = DEFAULT_CONFIG,
  embedded = false,
  hideChromeTitle = false,
}) => {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newParent, setNewParent] = useState("");
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState("");
  const [selectedParentFilter, setSelectedParentFilter] = useState("");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [pendingParents, setPendingParents] = useState({}); // Track pending parent changes
  const [updatingParent, setUpdatingParent] = useState(null); // Track which entity is being updated
  const [editingCapability, setEditingCapability] = useState(null); // Track which capability is being edited
  const [editName, setEditName] = useState(""); // Edit form name
  const [editDescription, setEditDescription] = useState(""); // Edit form description
  const [deletingCapability, setDeletingCapability] = useState(null); // Track which capability is being deleted
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Show delete confirmation modal
  const [capabilityToDelete, setCapabilityToDelete] = useState(null); // Capability to delete
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [childrenLoadedForParentIds, setChildrenLoadedForParentIds] = useState(
    () => new Set()
  );
  const [loadingChildrenForNode, setLoadingChildrenForNode] = useState(null);
  const [parentCandidates, setParentCandidates] = useState([]);
  const hierarchyLoadEpoch = useRef(0);
  const selectedParentFilterRef = useRef(selectedParentFilter);
  const expandedNodesRef = useRef(expandedNodes);
  const parentCandidatesRef = useRef(parentCandidates);
  const capabilitiesRef = useRef(capabilities);
  const childrenLoadedRef = useRef(childrenLoadedForParentIds);

  selectedParentFilterRef.current = selectedParentFilter;
  expandedNodesRef.current = expandedNodes;
  parentCandidatesRef.current = parentCandidates;
  capabilitiesRef.current = capabilities;
  childrenLoadedRef.current = childrenLoadedForParentIds;

  const loadChildrenForNode = useCallback(
    async (parentId, { force = false, epoch = null } = {}) => {
      const key = parentId ?? null;
      if (!force && childrenLoadedRef.current.has(key)) return;
      const activeEpoch = epoch ?? hierarchyLoadEpoch.current;
      try {
        setLoadingChildrenForNode(parentId);
        const children = await getHierarchyNodes(cfg.entityType, parentId, true);
        if (activeEpoch !== hierarchyLoadEpoch.current) return;
        setCapabilities((prev) => {
          const byId = new Map(prev.map((c) => [c.id, c]));
          (children || []).forEach((c) => byId.set(c.id, c));
          return Array.from(byId.values());
        });
        setChildrenLoadedForParentIds((prev) => new Set(prev).add(key));
      } catch (e) {
        debugError("Error loading children:", e);
        setError(e.message || "Failed to load children");
      } finally {
        if (activeEpoch === hierarchyLoadEpoch.current) {
          setLoadingChildrenForNode(null);
        }
      }
    },
    [cfg.entityType]
  );

  const resolveFilterNode = useCallback(
    async (filterId) => {
      const fromCaps = capabilitiesRef.current.find((c) => c.id === filterId);
      if (fromCaps) return fromCaps;
      const fromCandidates = parentCandidatesRef.current.find(
        (c) => c.id === filterId
      );
      if (fromCandidates) {
        return {
          id: fromCandidates.id,
          name: fromCandidates.name || "",
          description: fromCandidates.description || null,
          parent: fromCandidates.parent || null,
          type: cfg.entityType,
        };
      }
      try {
        const res = await getEntities({
          type: cfg.entityType,
          page: 1,
          pageSize: 2000,
          sortBy: "name",
          sortDesc: false,
        });
        const match = (res?.entities || []).find((e) => e.id === filterId);
        if (!match) return null;
        return {
          id: match.id,
          name: match.name || "",
          description: match.description || null,
          parent: match.parent || null,
          type: cfg.entityType,
        };
      } catch (e) {
        debugWarn("Failed to resolve filter node:", e);
        return null;
      }
    },
    [cfg.entityType]
  );

  const restoreSubtreeFilter = useCallback(
    async (filterId, epoch) => {
      const filterNode = await resolveFilterNode(filterId);
      if (epoch !== hierarchyLoadEpoch.current) return false;
      if (!filterNode) {
        setSelectedParentFilter("");
        setExpandedNodes(new Set());
        return false;
      }

      setCapabilities((prev) => {
        const byId = new Map(prev.map((c) => [c.id, c]));
        byId.set(filterNode.id, { ...byId.get(filterNode.id), ...filterNode });
        return Array.from(byId.values());
      });

      await loadChildrenForNode(filterId, { force: true, epoch });
      if (epoch !== hierarchyLoadEpoch.current) return false;

      const priorExpanded = Array.from(expandedNodesRef.current).filter(
        (id) => id && id !== filterId
      );
      setExpandedNodes(new Set([filterId, ...priorExpanded]));

      for (const nodeId of priorExpanded) {
        await loadChildrenForNode(nodeId, { force: true, epoch });
        if (epoch !== hierarchyLoadEpoch.current) return false;
      }
      return true;
    },
    [loadChildrenForNode, resolveFilterNode]
  );

  /** Drop API + Add-dialog tree caches so hierarchy UIs see fresh children. */
  const invalidateHierarchyCaches = useCallback(() => {
    try {
      invalidateHierarchyNodesCache(cfg.entityType);
      clearHierarchyTreeSessionCache(cfg.entityType);
    } catch (_) {
      /* ignore */
    }
  }, [cfg.entityType]);

  /**
   * Ensure a parent and its ancestors are in local tree state, load each level,
   * and expand the path so a newly created child is visible under its parent.
   * Avoids the post-create "wipe to roots" path that could hide nested creates.
   */
  const ensureParentPathVisible = useCallback(
    async (parentId, epoch) => {
      if (!parentId) {
        await loadChildrenForNode(null, { force: true, epoch });
        return;
      }

      const resolveNode = async (nodeId) => {
        const fromCaps = capabilitiesRef.current.find((c) => c.id === nodeId);
        if (fromCaps) return fromCaps;
        const fromCandidates = parentCandidatesRef.current.find(
          (c) => c.id === nodeId
        );
        if (fromCandidates) {
          return {
            id: fromCandidates.id,
            name: fromCandidates.name || "",
            description: fromCandidates.description || null,
            parent: fromCandidates.parent || null,
            type: cfg.entityType,
          };
        }
        return resolveFilterNode(nodeId);
      };

      const chain = []; // root → … → parentId
      let cur = parentId;
      const seen = new Set();
      while (cur && !seen.has(cur)) {
        seen.add(cur);
        const node = await resolveNode(cur);
        if (epoch !== hierarchyLoadEpoch.current) return;
        if (!node) break;
        chain.unshift(node);
        cur = node.parent || null;
      }

      if (chain.length) {
        setCapabilities((prev) => {
          const byId = new Map(prev.map((c) => [c.id, c]));
          chain.forEach((n) => {
            byId.set(n.id, {
              ...(byId.get(n.id) || {}),
              id: n.id,
              name: n.name || "",
              description: n.description || null,
              parent: n.parent || null,
              type: cfg.entityType,
            });
          });
          return Array.from(byId.values());
        });
      }

      // Roots + each ancestor's children so the path can render.
      await loadChildrenForNode(null, { force: true, epoch });
      if (epoch !== hierarchyLoadEpoch.current) return;
      for (const node of chain) {
        await loadChildrenForNode(node.id, { force: true, epoch });
        if (epoch !== hierarchyLoadEpoch.current) return;
      }

      setExpandedNodes((prev) => {
        const next = new Set(prev);
        chain.forEach((n) => next.add(n.id));
        return next;
      });
    },
    [cfg.entityType, loadChildrenForNode, resolveFilterNode]
  );

  const fetchCapabilities = useCallback(
    async (forceRefresh = false) => {
      const epoch = ++hierarchyLoadEpoch.current;
      const filterId = selectedParentFilterRef.current;
      try {
        setLoading(true);
        const roots = await getHierarchyNodes(
          cfg.entityType,
          null,
          forceRefresh
        );
        if (epoch !== hierarchyLoadEpoch.current) return;
        debugLog(`📦 Fetched ${roots?.length || 0} root capabilities`);

        setCapabilities(roots || []);
        setChildrenLoadedForParentIds(new Set([null]));
        setPendingParents({});

        if (filterId) {
          await restoreSubtreeFilter(filterId, epoch);
        } else {
          setExpandedNodes(new Set());
        }
      } catch (e) {
        debugError("Error fetching capabilities:", e);
        setError(e.message || "Failed to load capabilities");
      } finally {
        if (epoch === hierarchyLoadEpoch.current) setLoading(false);
      }
    },
    [cfg.entityType, restoreSubtreeFilter]
  );

  const pruneCapabilitiesFromTree = useCallback((deletedId) => {
    setCapabilities((prev) => {
      const removeIds = collectDescendantIds(deletedId, prev);
      setExpandedNodes((exp) => {
        const next = new Set(exp);
        removeIds.forEach((id) => next.delete(id));
        return next;
      });
      setChildrenLoadedForParentIds((loaded) => {
        const next = new Set(loaded);
        removeIds.forEach((id) => next.delete(id));
        return next;
      });
      return prev.filter((c) => !removeIds.has(c.id));
    });
  }, []);

  const refreshParentCandidates = useCallback(async () => {
    try {
      const res = await getEntities({
        type: cfg.entityType,
        page: 1,
        pageSize: 2000,
        sortBy: "name",
        sortDesc: false,
      });
      const list = (res?.entities || [])
        .map((e) => ({
          id: e.id,
          name: e.name || "",
          parent: e.parent || null,
        }))
        .filter((e) => e.id);
      list.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
      );
      setParentCandidates(list);
    } catch (e) {
      debugWarn("Failed to load parent candidates:", e);
    }
  }, [cfg.entityType]);

  useEffect(() => {
    fetchCapabilities();
    refreshParentCandidates();
  }, [fetchCapabilities, refreshParentCandidates]);

  const parentOptionsSorted = useMemo(() => {
    const byId = new Map();
    (parentCandidates || []).forEach((c) => {
      if (c?.id) byId.set(c.id, c);
    });
    (capabilities || []).forEach((c) => {
      if (c?.id && !byId.has(c.id)) {
        byId.set(c.id, { id: c.id, name: c.name || "", parent: c.parent || null });
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })
    );
  }, [parentCandidates, capabilities]);

  const capabilityById = useMemo(
    () => Object.fromEntries((capabilities || []).map((c) => [c.id, c])),
    [capabilities]
  );

  // Filter capabilities by search query
  const filteredCapabilities = useMemo(() => {
    if (!searchQuery.trim()) return capabilities;
    const query = searchQuery.toLowerCase();
    return capabilities.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
    );
  }, [capabilities, searchQuery]);

  // Build forest (multiple roots) using parent field
  const forest = useMemo(() => {
    const childrenMap = {}; // id -> children[]
    filteredCapabilities.forEach((c) => {
      childrenMap[c.id] = [];
    });
    filteredCapabilities.forEach((c) => {
      if (c.parent && childrenMap[c.parent]) childrenMap[c.parent].push(c);
    });
    const roots = filteredCapabilities.filter(
      (c) => !c.parent || !capabilityById[c.parent]
    );
    return { roots, childrenMap };
  }, [filteredCapabilities, capabilityById]);

  const displayRoots = useMemo(() => {
    if (selectedParentFilter) {
      const parent = capabilityById[selectedParentFilter];
      return parent ? [parent] : [];
    }
    return forest.roots;
  }, [selectedParentFilter, capabilityById, forest.roots]);

  const toggleExpanded = async (nodeId) => {
    if (expandedNodes.has(nodeId)) {
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        next.delete(nodeId);
        return next;
      });
      return;
    }
    await loadChildrenForNode(nodeId);
    setExpandedNodes((prev) => new Set(prev).add(nodeId));
  };

  const handleParentChange = (childId, newParentId) => {
    // Just update the pending selection, don't save yet
    setPendingParents((prev) => ({
      ...prev,
      [childId]: newParentId,
    }));
  };

  const handleSetParent = async (childId, parentId) => {
    try {
      setUpdatingParent(childId);
      setError(""); // Clear previous errors

      const newParent = parentId || null;
      debugLog(
        `🔄 Updating parent for entity ${childId} to ${newParent || "null"}`
      );
      const result = await updateEntity(childId, { parent: newParent });
      debugLog(`✅ Parent update result:`, result);

      // Clear pending parent for this node
      setPendingParents((prev) => {
        const newPending = { ...prev };
        delete newPending[childId];
        return newPending;
      });

      // Keep the node in local state with the new parent. A roots-only refresh
      // would hide reparented nodes until the user expands the new parent.
      setCapabilities((prev) => {
        const byId = new Map(prev.map((c) => [c.id, { ...c }]));
        const child = byId.get(childId);
        if (child) {
          byId.set(childId, { ...child, parent: newParent });
        }
        return Array.from(byId.values());
      });

      invalidateHierarchyCaches();
      const epoch = ++hierarchyLoadEpoch.current;
      if (newParent) {
        await ensureParentPathVisible(newParent, epoch);
      } else {
        await loadChildrenForNode(null, { force: true, epoch });
      }
      await refreshParentCandidates();
      debugLog(`✅ Parent update applied in tree`);
    } catch (e) {
      debugError(`❌ Error updating parent:`, e);
      setError(e.message || "Failed to update parent");
    } finally {
      setUpdatingParent(null);
    }
  };

  const poll = async (opId) => {
    let attempts = 0;
    while (attempts < 120) {
      const s = await getOperationStatus(opId);
      if (s.status === "completed" || s.status === "failed") return s;
      if (typeof s.progress === "number")
        setNotice(`Creating ${cfg.entityLabelLower}... ${s.progress}%`);
      await new Promise((r) => setTimeout(r, 300));
      attempts++;
    }
    return { status: "failed", error: "Timeout" };
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      setCreating(true);
      setNotice(`Creating ${cfg.entityLabelLower}... 10%`);
      const id = `${cfg.idPrefix}${Date.now()}`;
      const parentId = newParent || null;
      const nameTrimmed = newName.trim();
      const descTrimmed = newDescription.trim() || null;
      const res = await createEntityAsync({
        id,
        name: nameTrimmed,
        type: cfg.entityType,
        description: descTrimmed,
        parent: parentId,
      });
      const finishCreate = async (created) => {
        const createdId = created?.id || id;
        const createdParent =
          created?.parent != null && String(created.parent).trim() !== ""
            ? created.parent
            : parentId;
        const createdName = created?.name || nameTrimmed;

        setNotice(`${cfg.entityLabel} created successfully.`);
        setNewName("");
        setNewDescription("");
        setNewParent("");
        setShowCreatePanel(false);

        invalidateHierarchyCaches();

        // Optimistic insert so the node is present even before path reload finishes.
        setCapabilities((prev) => {
          const byId = new Map(prev.map((c) => [c.id, c]));
          byId.set(createdId, {
            ...(byId.get(createdId) || {}),
            id: createdId,
            name: createdName,
            description:
              created?.description !== undefined
                ? created.description
                : descTrimmed,
            parent: createdParent || null,
            type: cfg.entityType,
          });
          return Array.from(byId.values());
        });

        // One epoch for the whole refresh so in-flight loads don't cancel each other mid-path.
        const epoch = ++hierarchyLoadEpoch.current;
        await ensureParentPathVisible(createdParent || null, epoch);
        if (
          parentId &&
          createdParent &&
          String(createdParent) !== String(parentId)
        ) {
          setNotice(
            `"${createdName}" already existed under a different parent; showing its current location.`
          );
        }
        await refreshParentCandidates();
        setTimeout(() => setNotice(""), 2500);
      };
      if (res.operation_id) {
        const final = await poll(res.operation_id);
        if (final.status === "completed") {
          await finishCreate(final.result);
        } else {
          setNotice(`Failed to create: ${final.error || "Unknown error"}`);
          setTimeout(() => setNotice(""), 2500);
        }
      } else {
        await finishCreate(res.entity || res);
      }
    } catch (e) {
      setError(e.message || `Failed to create ${cfg.entityLabelLower}`);
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (capability) => {
    setEditingCapability(capability.id);
    setEditName(capability.name || "");
    setEditDescription(capability.description || "");
  };

  const handleSaveEdit = async () => {
    if (!editName.trim() || !editingCapability) return;
    try {
      setError("");
      await updateEntity(editingCapability, {
        name: editName.trim(),
        description: editDescription.trim() || null,
      });
      setNotice(cfg.updateSuccessLabel);
      setEditingCapability(null);
      setEditName("");
      setEditDescription("");
      invalidateHierarchyCaches();
      // Wait for cache invalidation to complete, then refresh
      await new Promise((resolve) => setTimeout(resolve, 500));
      await fetchCapabilities(true);
      setTimeout(() => setNotice(""), 2500);
    } catch (e) {
      setError(e.message || `Failed to update ${cfg.entityLabelLower}`);
    }
  };

  const handleCancelEdit = () => {
    setEditingCapability(null);
    setEditName("");
    setEditDescription("");
  };

  const handleDeleteClick = (capability) => {
    setCapabilityToDelete(capability);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!capabilityToDelete) return;
    const deletedId = capabilityToDelete.id;
    try {
      setDeletingCapability(deletedId);
      setError("");
      try {
        await deleteEntity(deletedId);
      } catch (e) {
        const status = e?.response?.status;
        const msg = String(e?.message || e?.response?.data?.detail || "");
        if (status !== 404 && !/404|not found/i.test(msg)) {
          throw e;
        }
      }
      const filterId = selectedParentFilterRef.current;
      const removedIds = collectDescendantIds(
        deletedId,
        capabilitiesRef.current
      );
      if (filterId && removedIds.has(filterId)) {
        selectedParentFilterRef.current = "";
        setSelectedParentFilter("");
      }
      hierarchyLoadEpoch.current += 1;
      pruneCapabilitiesFromTree(deletedId);
      setNotice(cfg.deleteSuccessLabel);
      setShowDeleteConfirm(false);
      setCapabilityToDelete(null);
      invalidateHierarchyCaches();
      await fetchCapabilities(true);
      await refreshParentCandidates();
      setTimeout(() => setNotice(""), 2500);
    } catch (e) {
      setError(e.message || `Failed to delete ${cfg.entityLabelLower}`);
    } finally {
      setDeletingCapability(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setCapabilityToDelete(null);
  };

  const renderNode = (node, depth = 0) => {
    const childList = forest.childrenMap[node.id] || [];
    const childrenLoaded = childrenLoadedForParentIds.has(node.id ?? null);
    const hasChildren = childrenLoaded
      ? childList.length > 0
      : true;
    const isExpanded = expandedNodes.has(node.id);
    const isLoadingChildren = loadingChildrenForNode === node.id;
    const indentLevel = Math.min(depth, 8); // Allow deeper org / strategy trees

    return (
      <div
        key={node.id}
        className="mb-2 transition-all duration-200"
        style={{ marginLeft: `${indentLevel * 24}px` }}
      >
        <div
          className={`
            group relative bg-gray-800 border border-gray-700 rounded-lg p-3
            hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/20
            transition-all duration-200
            ${depth === 0 ? "bg-gradient-to-r from-gray-800 to-gray-800/50" : ""}
          `}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              {/* Expand/Collapse Button */}
              {hasChildren ? (
                <button
                  onClick={() => toggleExpanded(node.id)}
                  disabled={isLoadingChildren}
                  className={`
                    mt-0.5 flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center
                    bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white
                    transition-colors duration-150 disabled:opacity-70 disabled:cursor-wait
                  `}
                >
                  {isLoadingChildren ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${
                        isExpanded ? "rotate-90" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  )}
                </button>
              ) : (
                <div className="w-6 flex-shrink-0" /> // Spacer for alignment
              )}

              {/* Capability Info */}
              <div className="flex-1 min-w-0">
                {editingCapability === node.id ? (
                  /* Edit Mode */
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder={`${cfg.entityLabel} name`}
                      autoFocus
                    />
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
                      placeholder="Description (optional)"
                      rows={2}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={!editName.trim()}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded font-medium transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />
                      <h3 className="text-white font-semibold text-sm truncate">
                        {node.name}
                      </h3>
                      {hasChildren && childrenLoaded && childList.length > 0 && (
                        <span className="flex-shrink-0 text-xs text-gray-400 bg-gray-700 px-2 py-0.5 rounded-full">
                          {childList.length}{" "}
                          {childList.length === 1 ? "child" : "children"}
                        </span>
                      )}
                      {node.description && (
                        <span className="flex-shrink-0 text-xs text-gray-500 truncate max-w-xs">
                          {node.description}
                        </span>
                      )}
                    </div>
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handleEdit(node)}
                        className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded font-medium transition-colors flex items-center gap-1"
                        title={`Edit ${cfg.entityLabelLower}`}
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(node)}
                        disabled={deletingCapability === node.id}
                        className="px-2 py-1 bg-red-700 hover:bg-red-600 text-white text-xs rounded font-medium transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={`Delete ${cfg.entityLabelLower}`}
                      >
                        {deletingCapability === node.id ? (
                          <svg
                            className="animate-spin w-3 h-3"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        )}
                        Delete
                      </button>
                    </div>
                  </>
                )}

                {/* Parent Selector - Compact (only show in view mode) */}
                {editingCapability !== node.id && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-400 flex-shrink-0">
                      Parent:
                    </label>
                    <select
                      value={
                        pendingParents[node.id] !== undefined
                          ? pendingParents[node.id]
                          : node.parent || ""
                      }
                      onChange={(e) =>
                        handleParentChange(node.id, e.target.value)
                      }
                      disabled={updatingParent === node.id}
                      className="
                      flex-1 text-xs bg-gray-700 border border-gray-600 text-white rounded px-2 py-1
                      hover:border-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500
                      transition-colors duration-150
                      disabled:opacity-50 disabled:cursor-not-allowed
                    "
                    >
                      <option value="">⚡ Top Level</option>
                      {parentOptionsSorted
                        .filter((c) => c.id !== node.id)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    {pendingParents[node.id] !== undefined &&
                      pendingParents[node.id] !== (node.parent || "") && (
                        <button
                          onClick={() =>
                            handleSetParent(
                              node.id,
                              pendingParents[node.id] || null
                            )
                          }
                          disabled={updatingParent === node.id}
                          className="
                        px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded
                        font-medium transition-colors duration-150
                        disabled:opacity-50 disabled:cursor-not-allowed
                        flex items-center gap-1
                      "
                        >
                          {updatingParent === node.id ? (
                            <>
                              <svg
                                className="animate-spin w-3 h-3"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              Updating...
                            </>
                          ) : (
                            "Update"
                          )}
                        </button>
                      )}
                  </div>
                )}
              </div>
            </div>

            {/* Depth Indicator */}
            {depth > 0 && (
              <div className="flex-shrink-0 flex items-center gap-1">
                {Array.from({ length: depth }, (_, i) => (
                  <div
                    key={i}
                    className="w-1 h-1 rounded-full bg-blue-500/30"
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="mt-2 border-l-2 border-gray-700/50 ml-3 pl-2">
            {childList.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const actionButtons = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => setShowCreatePanel(!showCreatePanel)}
        className="
          px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
          font-medium text-sm transition-all duration-200
          shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40
          flex items-center gap-2
        "
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        {showCreatePanel ? "Cancel" : cfg.newButtonLabel}
      </button>
      <button
        type="button"
        onClick={() => setShowExcelImport(true)}
        className="
          px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg
          font-medium text-sm transition-colors duration-200
          flex items-center gap-2
        "
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        Import from Excel
      </button>
      <button
        type="button"
        onClick={fetchCapabilities}
        className="
          px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg
          font-medium text-sm transition-colors duration-200
          flex items-center gap-2
        "
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        Refresh
      </button>
    </div>
  );

  return (
    <div
      className={`${
        embedded ? "min-h-[28rem]" : "min-h-screen"
      } bg-gray-900 flex`}
    >
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div
          className={`bg-gradient-to-r from-gray-800 to-gray-850 border-b border-gray-700 px-6 ${
            hideChromeTitle ? "py-3" : "py-4"
          }`}
        >
          <div
            className={`flex ${
              hideChromeTitle
                ? "items-center justify-end"
                : "items-center justify-between"
            } gap-4 flex-wrap`}
          >
            {!hideChromeTitle ? (
              <div>
                <h1 className="text-2xl font-bold text-white mb-1">
                  {cfg.title}
                </h1>
                <p className="text-sm text-gray-400">{cfg.subtitle}</p>
              </div>
            ) : null}
            {actionButtons}
          </div>
        </div>

        {showExcelImport && (
          <HierarchyExcelImport
            entityType={cfg.entityType}
            idPrefix={cfg.idPrefix}
            entityLabel={cfg.entityLabel}
            entityLabelPlural={cfg.entityLabelPlural}
            onSuccess={async () => {
              invalidateHierarchyCaches();
              await fetchCapabilities(true);
              await refreshParentCandidates();
            }}
            onClose={() => setShowExcelImport(false)}
          />
        )}

        {/* Alerts */}
        {notice && (
          <div
            className={`mx-6 mt-4 p-3 rounded-lg shadow-lg ${
              creating
                ? "bg-blue-900/50 text-blue-200 border border-blue-700"
                : "bg-green-900/50 text-green-200 border border-green-700"
            }`}
          >
            <div className="flex items-center gap-2">
              {creating ? (
                <svg
                  className="animate-spin w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span className="font-medium">{notice}</span>
            </div>
          </div>
        )}
        {error && (
          <div className="mx-6 mt-4 p-3 rounded-lg bg-red-900/50 text-red-200 border border-red-700 shadow-lg">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="px-6 py-4 bg-gray-800/50 border-b border-gray-700">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={cfg.searchPlaceholder}
                className="
                  w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 pl-10
                  text-white placeholder-gray-400
                  focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20
                  transition-all duration-200
                "
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Filter by Parent */}
            <div className="w-64">
              <select
                value={selectedParentFilter}
                onChange={async (e) => {
                  const value = e.target.value;
                  selectedParentFilterRef.current = value;
                  setSelectedParentFilter(value);
                  if (value) {
                    const candidate = parentOptionsSorted.find(
                      (c) => c.id === value
                    );
                    if (candidate) {
                      setCapabilities((prev) => {
                        if (prev.some((c) => c.id === value)) return prev;
                        return [
                          ...prev,
                          {
                            id: candidate.id,
                            name: candidate.name || "",
                            description: null,
                            parent: candidate.parent || null,
                            type: cfg.entityType,
                          },
                        ];
                      });
                    }
                    await loadChildrenForNode(value);
                    setExpandedNodes((prev) => new Set(prev).add(value));
                  }
                }}
                className="
                  w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2
                  text-white text-sm
                  focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20
                  transition-all duration-200
                "
              >
                <option value="">{cfg.allLabel}</option>
                {parentOptionsSorted.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Expand/Collapse All */}
            <button
              onClick={() => {
                if (expandedNodes.size === filteredCapabilities.length) {
                  setExpandedNodes(new Set());
                } else {
                  setExpandedNodes(
                    new Set(filteredCapabilities.map((c) => c.id))
                  );
                }
              }}
              className="
                px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg
                text-sm font-medium transition-colors duration-200
                flex items-center gap-2
              "
            >
              {expandedNodes.size === filteredCapabilities.length
                ? "Collapse All"
                : "Expand All"}
            </button>
          </div>
        </div>

        {/* Hierarchy Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <svg
                  className="animate-spin w-12 h-12 text-blue-500 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-gray-400">{cfg.loadingLabel}</p>
              </div>
            </div>
          ) : (
            <div>
              {displayRoots.length === 0 ? (
                <div className="text-center py-16">
                  <div className="inline-block p-4 bg-gray-800 rounded-full mb-4">
                    <svg
                      className="w-12 h-12 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-400 text-lg mb-2">
                    {searchQuery
                      ? cfg.emptySearchLabel
                      : cfg.emptyLabel}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {searchQuery
                      ? "Try adjusting your search query"
                      : cfg.emptyHint}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => setShowCreatePanel(true)}
                      className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      {cfg.createFirstLabel}
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <div className="mb-4 text-sm text-gray-400">
                    {displayRoots.length === 1
                      ? `1 root ${cfg.entityLabelLower}`
                      : `${displayRoots.length} root ${cfg.entityLabelPlural}`}
                    {searchQuery && (
                      <span className="ml-2">
                        • {filteredCapabilities.length} total matching
                      </span>
                    )}
                  </div>
                  {displayRoots.map((root) => renderNode(root))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Panel - Slide-out Sidebar */}
      {showCreatePanel && (
        <div className="w-96 bg-gray-800 border-l border-gray-700 flex flex-col shadow-2xl">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-xl font-bold text-white mb-1">
              {cfg.createPanelTitle}
            </h2>
            <p className="text-sm text-gray-400">
              {cfg.createPanelSubtitle}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {cfg.nameLabel} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && newName.trim()) {
                      handleCreate();
                    }
                  }}
                  placeholder="e.g., Customer Management"
                  className="
                    w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2
                    text-white placeholder-gray-400
                    focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20
                    transition-all duration-200
                  "
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder={`Optional description for this ${cfg.entityLabelLower}...`}
                  rows={4}
                  className="
                    w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2
                    text-white placeholder-gray-400 text-sm
                    focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20
                    transition-all duration-200
                    resize-y
                  "
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {cfg.parentLabel}
                </label>
                <select
                  value={newParent}
                  onChange={(e) => setNewParent(e.target.value)}
                  className="
                    w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2
                    text-white text-sm
                    focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20
                    transition-all duration-200
                  "
                >
                  <option value="">⚡ Top Level (No Parent)</option>
                  {parentOptionsSorted.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-400">
                  {`Select a parent ${cfg.entityLabelLower} to create a hierarchy. Leave empty`}
                  for top-level.
                </p>
              </div>

              {/* Create Button - Moved here to appear after Parent dropdown */}
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={handleCreate}
                  disabled={creating || !newName.trim()}
                  className={`
                    flex-1 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200
                    ${
                      creating || !newName.trim()
                        ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40"
                    }
                  `}
                >
                  {creating ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    cfg.createActionLabel
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowCreatePanel(false);
                    setNewName("");
                    setNewDescription("");
                    setNewParent("");
                  }}
                  className="
                    px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg
                    font-medium text-sm transition-colors duration-200
                  "
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && capabilityToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-900/50 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {`Delete ${cfg.entityLabel}`}
                </h3>
                <p className="text-sm text-gray-400">
                  This action cannot be undone
                </p>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-gray-300 mb-2">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-white">
                  "{capabilityToDelete.name}"
                </span>
                ?
              </p>
              {forest.childrenMap[capabilityToDelete.id]?.length > 0 && (
                <div className="mt-3 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded text-yellow-200 text-sm">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="font-medium">Warning</p>
                      <p>
                        This {cfg.entityLabelLower} has{" "}
                        {forest.childrenMap[capabilityToDelete.id].length}{" "}
                        {forest.childrenMap[capabilityToDelete.id].length === 1
                          ? "child"
                          : "children"}
                        {`. Deleting it will also remove all child ${cfg.entityLabelPlural}.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={deletingCapability === capabilityToDelete.id}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingCapability === capabilityToDelete.id}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deletingCapability === capabilityToDelete.id ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapabilityHierarchyBuilder;
